/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import {
  getAttributesForm,
  getCustomer,
  updateCustomerEmail,
  updateCustomer,
} from '@/account/api';
import {
  KeysSortOrderProps,
  UseCustomerInformationProps,
} from '@/account/types';
import { defaultAccountAttributePreset } from '@/account/configs/defaultAttributePreset.config';
import { CustomerDataModelShort } from '@/account/data/models/customer';
import { AttributesFormModel } from '@/account/data/models';
import { getFormValues } from '@/account/lib/getFormValues';
import { useText } from '@adobe-commerce/elsie/i18n';
import { normalizeAttributes } from '@/account/lib/normalizeAttributes';
import { transformDobCustomer } from '@/account/lib/transformDobCustomer';
import { EventsList, publishEvents } from '@/account/lib/acdl';

export const useCustomerInformation = ({
  handleSetInLineAlert,
}: UseCustomerInformationProps) => {
  const translations = useText({
    accountSuccess:
      'Account.minifiedView.CustomerInformation.editCustomerInformation.accountSuccess',
    accountError:
      'Account.minifiedView.CustomerInformation.editCustomerInformation.accountError',
    genderMale: 'Account.minifiedView.CustomerInformation.genderMale',
    genderFemale: 'Account.minifiedView.CustomerInformation.genderFemale',
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [showChangePassword, setShowChangePassword] = useState<boolean>(false);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [errorPasswordEmpty, setErrorPasswordEmpty] = useState(false);
  const [showPasswordOnEmailChange, setShowPasswordOnEmailChange] =
    useState(false);
  const [keysSortOrder, setKeysSortOrder] = useState<KeysSortOrderProps[] | []>(
    []
  );
  const [customerData, setCustomerData] =
    useState<CustomerDataModelShort | null>(null);
  const [formFieldsList, setFormFieldsList] = useState<
    AttributesFormModel[] | []
  >([]);
  const [inputChange, setInputChange] = useState<
    Record<string, string | number | boolean>
  >({});
  const [passwordValue, setPasswordValue] = useState('');
  const [createdAt, setCreatedAt] = useState('');

  const handleOnBlurPassword = useCallback((event: Event) => {
    const { value } = event?.target as HTMLInputElement;

    if (value.length) setErrorPasswordEmpty(false);
    if (!value.length) setErrorPasswordEmpty(true);
  }, []);

  const handleSetPassword = useCallback((value: string) => {
    setPasswordValue(value);
  }, []);

  const handleInputChange = useCallback(
    (value: Record<string, string | number | boolean>) => {
      setInputChange(value);
    },
    []
  );

  const handleShowChangePassword = useCallback(() => {
    setShowChangePassword(true);
    setShowEditForm(false);
    handleSetInLineAlert();
    handleSetPassword('');
  }, [handleSetInLineAlert, handleSetPassword]);

  const handleHideChangePassword = useCallback((clearStates?: () => void) => {
    clearStates?.();
    setShowChangePassword(false);
  }, []);

  const handleShowEditForm = useCallback(() => {
    setShowEditForm(true);
    setShowChangePassword(false);
    handleSetInLineAlert();
    handleSetPassword('');
  }, [handleSetInLineAlert, handleSetPassword]);

  const handleHideEditForm = useCallback((clearStates?: () => void) => {
    clearStates?.();
    setShowEditForm(false);
  }, []);

  const renderAlertMessage = useCallback(
    (type: 'success' | 'error', message?: string) => {
      if (type === 'success') {
        handleSetInLineAlert({
          type: 'success',
          text: message ?? translations.accountSuccess,
        });
      } else if (type === 'error') {
        handleSetInLineAlert({
          type: 'error',
          text: message ?? translations.accountError,
        });
      } else {
        handleSetInLineAlert();
      }

      setSubmitLoading(false);
    },
    [handleSetInLineAlert, translations]
  );

  const fetchCustomer = useCallback(() => {
    getCustomer().then((customer: CustomerDataModelShort) => {
      const createAccountDate = customer?.createdAt?.split(' ')[0];
      const modifyCustomerData = transformDobCustomer({
        ...customer,
        gender:
          customer.gender === 1
            ? translations.genderMale
            : translations.genderFemale,
      });

      setCustomerData(modifyCustomerData);
      setCreatedAt(createAccountDate);
    });
  }, [translations.genderFemale, translations.genderMale]);

  useEffect(() => {
    fetchCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getAttributesForm('customer_account_edit').then(
      (attributesFormResponse) => {
        setFormFieldsList(attributesFormResponse);

        const result: KeysSortOrderProps[] = attributesFormResponse.map(
          ({ name, customUpperCode, orderNumber, label }) => ({
            name: customUpperCode,
            orderNumber,
            label: !defaultAccountAttributePreset.includes(name as string)
              ? label
              : null,
          })
        );

        setKeysSortOrder(result);
      }
    );
  }, []);

  useEffect(() => {
    if (inputChange['email'] && inputChange['email'] !== customerData?.email) {
      setShowPasswordOnEmailChange(true);
    } else if (
      inputChange['email'] &&
      inputChange['email'] === customerData?.email
    ) {
      setShowPasswordOnEmailChange(false);
    }
  }, [customerData?.email, inputChange]);

  const orderedCustomerData = useMemo(() => {
    if (!keysSortOrder || !customerData) return [];

    const result = keysSortOrder
      .filter(
        ({ name }) =>
          name !== undefined && (customerData as Record<string, any>)[name]
      )
      .map((item) => {
        return {
          name: item.name,
          orderNumber: item.orderNumber,
          value: (customerData as Record<string, any>)[item.name],
          label: item.label,
        };
      });

    return result;
  }, [customerData, keysSortOrder]);

  useEffect(() => {
    orderedCustomerData?.length && setLoading(false);
  }, [orderedCustomerData]);

  const normalizeFieldsConfig = useMemo(() => {
    return formFieldsList
      ?.map((el) => ({
        ...el,
        defaultValue:
          el?.customUpperCode && customerData
            ? (customerData as Record<string, any>)[el.customUpperCode] ?? ''
            : '',
      }))
      .map((item) => {
        if (item.customUpperCode === 'gender') {
          return {
            ...item,
            defaultValue: item.defaultValue === 'Male' ? 1 : 2,
          };
        }

        return item;
      });
  }, [formFieldsList, customerData]);

  const handleUpdateCustomerInformation = useCallback(
    async (event: Event, valid: boolean) => {
      const formValues = getFormValues(event.target as HTMLFormElement);

      const { email, password, ...props } = formValues;

      const passwordEmpty =
        email !== customerData?.email! && password.length === 0;

      if (!valid) {
        if (passwordEmpty) {
          setErrorPasswordEmpty(true);
        }
        return;
      }

      setErrorPasswordEmpty(false);
      setSubmitLoading(true);

      if (email === customerData?.email!) {
        handleSetPassword('');

        updateCustomer(normalizeAttributes(props, 'account'))
          .then((response) => {
            if (!response) return;

            fetchCustomer();

            renderAlertMessage('success');
            publishEvents(EventsList?.EDIT_ACCOUNT_EVENT, {
              ...formValues,
            });
          })
          .catch((error) => {
            renderAlertMessage('error', error.message);
          });

        return;
      }

      if (passwordEmpty) {
        setErrorPasswordEmpty(true);
        setSubmitLoading(false);
        return;
      }

      if (email?.length && password?.length) {
        updateCustomerEmail({ email, password })
          .then((customerEmail) => {
            if (!customerEmail) return;

            updateCustomer(normalizeAttributes(props, 'account'))
              .then((responseCustomer) => {
                if (!responseCustomer) return;

                fetchCustomer();

                renderAlertMessage('success');

                publishEvents(EventsList?.EDIT_ACCOUNT_EVENT, {
                  ...formValues,
                });
              })
              .catch((error) => {
                renderAlertMessage('error', error.message);
              });
          })
          .catch((error) => {
            renderAlertMessage('error', error.message);
          });
      }
    },
    [customerData, handleSetPassword, fetchCustomer, renderAlertMessage]
  );

  return {
    createdAt,
    errorPasswordEmpty,
    passwordValue,
    showPasswordOnEmailChange,
    orderedCustomerData,
    loading,
    normalizeFieldsConfig,
    submitLoading,
    showEditForm,
    showChangePassword,
    handleShowChangePassword,
    handleHideChangePassword,
    handleShowEditForm,
    handleHideEditForm,
    handleUpdateCustomerInformation,
    handleInputChange,
    handleSetPassword,
    handleOnBlurPassword,
    renderAlertMessage,
  };
};
